import {
    createContext,
    useState,
    useContext,
    ReactElement,
    ReactNode
} from 'react'
import {
    AppContextType,
    ExtraActionWhenCloseModalType
} from './casa-ideas-types'

export const AppContext = createContext<AppContextType | null>(null)

export function useModalGlobalState(): AppContextType {
    const context = useContext(AppContext)

    if (!context) {
        throw new Error(
            'useModalGlobalState must be used within a ModalStateProvider'
        )
    }

    return context
}

export const ModalStateProvider = ({
    children
}: {
    children: ReactNode
}): ReactElement => {
    const [modalOpened, setModalOpened] = useState(false)
    const [modalContent, setModalContent] = useState<ReactNode>(null)
    const [extraActionWhenCloseModal, setExtraActionWhenCloseModal] =
        useState<ExtraActionWhenCloseModalType | null>(null)

    const modalOpen = (
        content: ReactNode,
        extraCloseAction: ExtraActionWhenCloseModalType | null
    ) => {
        setModalContent(content)

        // Wrapping avoids React treating the callback as a useState updater
        // (which would invoke it immediately and close the shop-look modal).
        if (extraCloseAction) {
            setExtraActionWhenCloseModal(() => extraCloseAction)
        }

        window.setTimeout(() => {
            setModalOpened(true)
        }, 10) // timeout needed to perform css transitions
    }

    const modalClose = () => {
        setModalOpened(false)

        if (extraActionWhenCloseModal) {
            extraActionWhenCloseModal()
            setExtraActionWhenCloseModal(null)
        }

        window.setTimeout(() => {
            setModalContent(null)
        }, 200) // timeout needed to perform css transitions
    }

    const modalCloseWithExtraAction = (action: () => void) => {
        setExtraActionWhenCloseModal(() => action)
        modalClose()
    }

    return (
        <AppContext.Provider
            value={{
                state: {
                    modalOpened,
                    modalContent,
                    extraActionWhenCloseModal
                },
                modalOpen,
                modalClose,
                modalCloseWithExtraAction
            }}
        >
            {children}
        </AppContext.Provider>
    )
}
